import { Input } from '@pique/component';
import type { ConfirmModalProps } from '@pique/component/ui/modal';
import { ConfirmModal } from '@pique/component/ui/modal';
import { useWorkspaceInfo } from '@pique/core/hooks/use-workspace-info';
import { UNTITLED_WORKSPACE_NAME } from '@pique/env/constant';
import { WorkspaceFlavour } from '@pique/env/workspace';
import { Trans, useI18n } from '@pique/i18n';
import type { WorkspaceMetadata } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import * as styles from './style.css';

export interface WorkspaceDeleteProps extends ConfirmModalProps {
  workspaceMetadata: WorkspaceMetadata;
  onConfirm?: (() => void) | (() => Promise<void>);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WorkspaceDeleteModal = ({
  workspaceMetadata,
  onConfirm,
  ...props
}: WorkspaceDeleteProps) => {
  const [deleteStr, setDeleteStr] = useState<string>('');
  const info = useWorkspaceInfo(workspaceMetadata);
  const workspaceName = info?.name ?? UNTITLED_WORKSPACE_NAME;
  const allowDelete = deleteStr === workspaceName;
  const t = useI18n();

  const handleOnEnter = useCallback(() => {
    if (allowDelete) {
      return onConfirm?.();
    }
  }, [allowDelete, onConfirm]);

  return (
    <ConfirmModal
      title={`${t['com.pique.workspaceDelete.title']()}?`}
      cancelText={t['com.pique.workspaceDelete.button.cancel']()}
      confirmText={t['com.pique.workspaceDelete.button.delete']()}
      confirmButtonOptions={{
        variant: 'error',
        disabled: !allowDelete,
        ['data-testid' as string]: 'delete-workspace-confirm-button',
      }}
      {...props}
      onConfirm={onConfirm}
    >
      {workspaceMetadata.flavour === WorkspaceFlavour.LOCAL ? (
        <Trans i18nKey="com.pique.workspaceDelete.description">
          Deleting (
          <span className={styles.workspaceName}>
            {{ workspace: workspaceName } as any}
          </span>
          ) cannot be undone, please proceed with caution. All contents will be
          lost.
        </Trans>
      ) : (
        <Trans i18nKey="com.pique.workspaceDelete.description2">
          Deleting (
          <span className={styles.workspaceName}>
            {{ workspace: workspaceName } as any}
          </span>
          ) will delete both local and cloud data, this operation cannot be
          undone, please proceed with caution.
        </Trans>
      )}
      <div className={styles.inputContent}>
        <Input
          autoFocus
          onChange={setDeleteStr}
          data-testid="delete-workspace-input"
          onEnter={handleOnEnter}
          placeholder={t['com.pique.workspaceDelete.placeholder']()}
          size="large"
        />
      </div>
    </ConfirmModal>
  );
};
